<div class="container mx-auto space-y-6">
  <!-- Page Header -->
  <div
    class="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-base-300"
  >
    <div>
      <h1 class="text-2xl font-bold text-base-content flex items-center gap-2">
        <i class="fa-solid fa-user-graduate text-primary"></i>
        Student Management
      </h1>
      <p class="text-sm text-base-content/60 mt-1">
        Manage students and track invitations across your academy
      </p>
    </div>
    <div class="flex items-center gap-2">
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-btn-outline gap-2"
        (click)="activeTab === 'STUDENTS' ? loadStudents() : loadInvitations()"
        [disabled]="loadingStudents || loadingInvitations"
      >
        <i
          class="fa-solid fa-arrows-rotate"
          [class.animate-spin]="loadingStudents || loadingInvitations"
        ></i>
        Refresh
      </button>
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-btn-outline daisy-btn-primary gap-2"
        (click)="openAddManualStudentModal()"
      >
        <i class="fa-solid fa-user-plus"></i>
        Add Student
      </button>
      <button
        type="button"
        class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2 shadow-sm"
        (click)="showInviteModal = true"
      >
        <i class="fa-solid fa-paper-plane"></i>
        Invite Students
      </button>
    </div>
  </div>

  <!-- Summary KPI Cards -->
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    <!-- Total Students -->
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm">
      <div class="daisy-card-body p-4 flex-row items-center gap-4">
        <div
          class="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-xl shrink-0"
        >
          <i class="fa-solid fa-users"></i>
        </div>
        <div>
          <p
            class="text-xs font-semibold text-base-content/60 uppercase tracking-wider"
          >
            Total Students
          </p>
          <h3 class="text-2xl font-bold text-base-content">
            {{ totalStudents }}
          </h3>
        </div>
      </div>
    </div>

    <!-- Active / Verified -->
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm">
      <div class="daisy-card-body p-4 flex-row items-center gap-4">
        <div
          class="w-12 h-12 rounded-xl bg-success/10 text-success flex items-center justify-center text-xl shrink-0"
        >
          <i class="fa-solid fa-user-check"></i>
        </div>
        <div>
          <p
            class="text-xs font-semibold text-base-content/60 uppercase tracking-wider"
          >
            Active Students
          </p>
          <h3 class="text-2xl font-bold text-base-content">
            {{ totalStudents - blockedStudentsCount }}
          </h3>
        </div>
      </div>
    </div>

    <!-- Blocked Students -->
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm">
      <div class="daisy-card-body p-4 flex-row items-center gap-4">
        <div
          class="w-12 h-12 rounded-xl bg-error/10 text-error flex items-center justify-center text-xl shrink-0"
        >
          <i class="fa-solid fa-user-slash"></i>
        </div>
        <div>
          <p
            class="text-xs font-semibold text-base-content/60 uppercase tracking-wider"
          >
            Blocked Students
          </p>
          <h3 class="text-2xl font-bold text-base-content">
            {{ blockedStudentsCount }}
          </h3>
        </div>
      </div>
    </div>

    <!-- Pending Invitations -->
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm">
      <div class="daisy-card-body p-4 flex-row items-center gap-4">
        <div
          class="w-12 h-12 rounded-xl bg-warning/10 text-warning flex items-center justify-center text-xl shrink-0"
        >
          <i class="fa-solid fa-envelope-open-text"></i>
        </div>
        <div>
          <p
            class="text-xs font-semibold text-base-content/60 uppercase tracking-wider"
          >
            Invitations
          </p>
          <h3 class="text-2xl font-bold text-base-content">
            {{ totalInvitations }}
          </h3>
        </div>
      </div>
    </div>
  </div>

  <!-- Tab Switcher -->
  <div class="flex items-center gap-2 border-b border-base-300 pb-2">
    <button
      type="button"
      class="daisy-btn daisy-btn-sm"
      [ngClass]="
        activeTab === 'STUDENTS' ? 'daisy-btn-primary' : 'daisy-btn-ghost'
      "
      (click)="setTab('STUDENTS')"
    >
      <i class="fa-solid fa-user-graduate mr-1"></i>
      Students ({{ totalStudents }})
    </button>
    <button
      type="button"
      class="daisy-btn daisy-btn-sm"
      [ngClass]="
        activeTab === 'INVITATIONS' ? 'daisy-btn-primary' : 'daisy-btn-ghost'
      "
      (click)="setTab('INVITATIONS')"
    >
      <i class="fa-solid fa-paper-plane mr-1"></i>
      Invitations ({{ totalInvitations }})
    </button>
  </div>

  <!-- TAB 1: STUDENTS DIRECTORY -->
  @if (activeTab === 'STUDENTS') {
    <div class="space-y-4">
      <!-- Search & Filters -->
      <div class="daisy-card bg-base-100 border border-base-200 shadow-sm">
        <div class="daisy-card-body p-4">
          <div class="flex flex-wrap gap-3">
            <div class="relative flex-1 min-w-48">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-base-content/40"
              >
                <i class="fa-solid fa-magnifying-glass"></i>
              </span>
              <input
                type="search"
                name="studentSearchQuery"
                autocomplete="off"
                data-lpignore="true"
                class="daisy-input daisy-input-sm daisy-input-bordered w-full pl-9 pr-8"
                placeholder="Search students by name, email, or mobile..."
                [ngModel]="studentSearch"
                (ngModelChange)="onStudentSearchChange($event)"
              />
              @if (studentSearch) {
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-base-content/40 hover:text-base-content"
                  (click)="clearStudentSearch()"
                  title="Clear search"
                >
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              }
            </div>
            <select
              class="daisy-select daisy-select-sm daisy-select-bordered min-w-36"
              [(ngModel)]="studentStatusFilter"
              (ngModelChange)="onStudentFilterChange()"
            >
              <option value="">All Statuses</option>
              <option value="VERIFIED">Verified / Active</option>
              <option value="UNVERIFIED">Unverified</option>
              <option value="BLOCKED">Blocked</option>
            </select>
            <select
              class="daisy-select daisy-select-sm daisy-select-bordered min-w-40"
              [(ngModel)]="selectedBatchFilter"
              (ngModelChange)="onBatchFilterChange()"
            >
              <option value="">All Batches</option>
              @for (b of batches; track b._id) {
                <option [value]="b._id">{{ b.name }}</option>
              }
            </select>
          </div>
        </div>
      </div>

      <!-- Students Table -->
      <div
        class="daisy-card bg-base-100 border border-base-200 shadow overflow-x-auto"
      >
        @if (loadingStudents) {
          <div class="flex flex-col items-center justify-center py-16 gap-3">
            <span
              class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"
            ></span>
            <p class="text-sm text-base-content/60">Loading students...</p>
          </div>
        } @else {
          <table class="daisy-table daisy-table-md">
            <thead>
              <tr class="bg-base-200/50">
                <th>Student</th>
                <th>Contact Info</th>
                <th>Status</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (student of students; track student._id) {
                <tr class="hover transition-colors">
                  <td>
                    <div class="flex items-center gap-3">
                      <div class="daisy-avatar daisy-placeholder">
                        <div
                          class="bg-primary/20 text-primary font-bold rounded-full w-10 h-10 flex items-center justify-center"
                        >
                          {{ getStudentInitial(student) }}
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center gap-1.5">
                          <p
                            class="font-semibold text-base-content leading-tight"
                          >
                            {{ getStudentName(student) }}
                          </p>
                          @if (student.isManuallyAdded) {
                            <span
                              class="daisy-badge daisy-badge-xs daisy-badge-warning gap-1 font-medium"
                              title="Manually onboarded offline student"
                            >
                              <i
                                class="fa-solid fa-clipboard-user text-[9px]"
                              ></i>
                              Manual
                            </span>
                          }
                        </div>
                        <p
                          class="text-xs text-base-content/50 mt-0.5 font-mono"
                        >
                          ID: {{ student.studentId || student._id }}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="space-y-0.5 text-sm">
                      @if (getStudentEmail(student); as email) {
                        <p
                          class="text-base-content flex items-center gap-1.5 text-xs"
                        >
                          <i
                            class="fa-solid fa-envelope text-xs text-base-content/40"
                          ></i>
                          {{ email }}
                        </p>
                      }
                      @if (getStudentMobile(student); as mobile) {
                        <p
                          class="text-xs text-base-content/60 flex items-center gap-1.5"
                        >
                          <i
                            class="fa-solid fa-phone text-xs text-base-content/40"
                          ></i>
                          {{ mobile }}
                        </p>
                      }
                      @if (
                        !getStudentEmail(student) && !getStudentMobile(student)
                      ) {
                        <p
                          class="text-base-content flex items-center gap-1.5 text-xs"
                        >
                          <i
                            class="fa-solid fa-user text-xs text-base-content/40"
                          ></i>
                          {{ student.username }}
                        </p>
                      }
                    </div>
                  </td>
                  <td>
                    <span
                      class="daisy-badge daisy-badge-sm font-medium {{
                        getStatusBadgeClass(student.status)
                      }}"
                    >
                      {{ student.status }}
                    </span>
                  </td>
                  <td class="text-right">
                    <div class="daisy-dropdown daisy-dropdown-end">
                      <button
                        tabindex="0"
                        class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square"
                        aria-label="Actions"
                      >
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      </button>
                      <ul
                        tabindex="0"
                        class="daisy-dropdown-content daisy-menu daisy-menu-sm bg-base-100 rounded-box z-10 w-44 p-2 shadow-lg border border-base-200"
                      >
                        <li>
                          <a (click)="openStudentDetails(student)">
                            <i class="fa-solid fa-eye w-4"></i> View Details
                          </a>
                        </li>
                        @if (student.isManuallyAdded) {
                          <li>
                            <a (click)="openAdminResetPassword(student)">
                              <i class="fa-solid fa-key w-4 text-warning"></i>
                              Reset Password
                            </a>
                          </li>
                        }
                        <li>
                          <a
                            (click)="toggleBlockStudent(student)"
                            [class.text-error]="student.status !== 'BLOCKED'"
                            [class.text-success]="student.status === 'BLOCKED'"
                          >
                            <i
                              class="fa-solid {{
                                student.status === 'BLOCKED'
                                  ? 'fa-lock-open'
                                  : 'fa-ban'
                              }} w-4"
                            ></i>
                            {{
                              student.status === 'BLOCKED'
                                ? 'Unblock Student'
                                : 'Block Student'
                            }}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="4" class="text-center py-16">
                    <div
                      class="flex flex-col items-center gap-3 text-base-content/40"
                    >
                      <i class="fa-solid fa-user-graduate text-4xl"></i>
                      <p class="text-base font-semibold">No students found</p>
                      <p class="text-xs max-w-sm">
                        Invite students using their email address or mobile
                        number to get started.
                      </p>
                      <button
                        type="button"
                        class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2 mt-2"
                        (click)="showInviteModal = true"
                      >
                        <i class="fa-solid fa-user-plus"></i> Invite First
                        Student
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>

          <!-- Pagination -->
          @if (totalStudents > studentLimit) {
            <div
              class="flex items-center justify-between px-4 py-3 border-t border-base-200"
            >
              <p class="text-xs text-base-content/60">
                Showing {{ (studentPage - 1) * studentLimit + 1 }} to
                {{ Math.min(studentPage * studentLimit, totalStudents) }} of
                {{ totalStudents }} students
              </p>
              <div class="daisy-join">
                <button
                  type="button"
                  class="daisy-join-item daisy-btn daisy-btn-sm"
                  [disabled]="studentPage <= 1"
                  (click)="onStudentPageChange(studentPage - 1)"
                >
                  «
                </button>
                <button
                  type="button"
                  class="daisy-join-item daisy-btn daisy-btn-sm daisy-btn-active"
                >
                  Page {{ studentPage }}
                </button>
                <button
                  type="button"
                  class="daisy-join-item daisy-btn daisy-btn-sm"
                  [disabled]="studentPage * studentLimit >= totalStudents"
                  (click)="onStudentPageChange(studentPage + 1)"
                >
                  »
                </button>
              </div>
            </div>
          }
        }
      </div>
    </div>
  }

  <!-- TAB 2: INVITATIONS TRACKING -->
  @if (activeTab === 'INVITATIONS') {
    <div class="space-y-4">
      <!-- Search & Status Filters -->
      <div class="daisy-card bg-base-100 border border-base-200 shadow-sm">
        <div class="daisy-card-body p-4">
          <div class="flex flex-wrap gap-3">
            <div class="relative flex-1 min-w-48">
              <span
                class="absolute inset-y-0 left-0 flex items-center pl-3 text-base-content/40"
              >
                <i class="fa-solid fa-magnifying-glass"></i>
              </span>
              <input
                type="search"
                name="invitationSearchQuery"
                autocomplete="off"
                data-lpignore="true"
                class="daisy-input daisy-input-sm daisy-input-bordered w-full pl-9 pr-8"
                placeholder="Search by recipient email, mobile or student name..."
                [ngModel]="invitationSearch"
                (ngModelChange)="onInvitationSearchChange($event)"
              />
              @if (invitationSearch) {
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-base-content/40 hover:text-base-content"
                  (click)="clearInvitationSearch()"
                  title="Clear search"
                >
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              }
            </div>
            <select
              class="daisy-select daisy-select-sm daisy-select-bordered min-w-36"
              [(ngModel)]="invitationStatusFilter"
              (ngModelChange)="onInvitationFilterChange()"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Invitations Table -->
      <div
        class="daisy-card bg-base-100 border border-base-200 shadow overflow-x-auto"
      >
        @if (loadingInvitations) {
          <div class="flex flex-col items-center justify-center py-16 gap-3">
            <span
              class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary"
            ></span>
            <p class="text-sm text-base-content/60">Loading invitations...</p>
          </div>
        } @else {
          <table class="daisy-table daisy-table-md">
            <thead>
              <tr class="bg-base-200/50">
                <th>Recipient</th>
                <th>Target Class</th>
                <th>Invited By</th>
                <th>Status</th>
                <th>Sent / Expires</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (invitation of invitations; track invitation._id) {
                <tr class="hover transition-colors">
                  <td>
                    <div class="flex items-center gap-2">
                      <span
                        class="w-8 h-8 rounded-lg bg-base-200 flex items-center justify-center text-sm text-primary shrink-0"
                      >
                        <i
                          class="fa-solid"
                          [ngClass]="
                            invitation.recipientType === 'EMAIL'
                              ? 'fa-envelope'
                              : 'fa-mobile-screen'
                          "
                        ></i>
                      </span>
                      <div>
                        <p class="font-semibold text-base-content text-sm">
                          {{ invitation.recipient }}
                        </p>
                        @if (invitation.studentName) {
                          <p class="text-xs text-base-content/60">
                            Name: {{ invitation.studentName }}
                          </p>
                        }
                      </div>
                    </div>
                  </td>
                  <td>
                    @if (invitation.liveClass) {
                      <span
                        class="daisy-badge daisy-badge-outline daisy-badge-sm font-medium"
                      >
                        {{ invitation.liveClass.title }}
                      </span>
                    } @else {
                      <span class="text-xs text-base-content/50"
                        >General Academy</span
                      >
                    }
                  </td>
                  <td>
                    <p class="text-xs font-medium text-base-content">
                      {{
                        invitation.invitedBy?.profile?.personal?.firstName
                          ? invitation.invitedBy?.profile?.personal?.firstName +
                            ' ' +
                            (invitation.invitedBy?.profile?.personal
                              ?.lastName || '')
                          : invitation.invitedBy?.username || 'Academy'
                      }}
                    </p>
                  </td>
                  <td>
                    <span
                      class="daisy-badge daisy-badge-sm font-medium {{
                        getStatusBadgeClass(invitation.status)
                      }}"
                    >
                      {{ invitation.status }}
                    </span>
                  </td>
                  <td>
                    <div class="text-xs space-y-0.5">
                      <p class="text-base-content/70">
                        {{ invitation.createdAt | date: 'shortDate' }}
                      </p>
                      <p class="text-base-content/40">
                        Expires {{ invitation.expiresAt | date: 'shortDate' }}
                      </p>
                    </div>
                  </td>
                  <td class="text-right">
                    <div class="flex items-center justify-end gap-1">
                      @if (
                        invitation.status === 'PENDING' ||
                        invitation.status === 'EXPIRED'
                      ) {
                        <button
                          type="button"
                          class="daisy-btn daisy-btn-ghost daisy-btn-xs gap-1 text-primary"
                          (click)="resendInvite(invitation)"
                          [disabled]="processingAction()"
                          title="Resend invitation email/SMS"
                        >
                          <i class="fa-solid fa-rotate-right"></i>
                          <span>Resend</span>
                        </button>
                      }
                      @if (invitation.status === 'PENDING') {
                        <button
                          type="button"
                          class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error"
                          (click)="cancelInvite(invitation)"
                          [disabled]="processingAction()"
                          title="Cancel invitation"
                        >
                          <i class="fa-solid fa-ban"></i>
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="6" class="text-center py-16">
                    <div
                      class="flex flex-col items-center gap-3 text-base-content/40"
                    >
                      <i class="fa-solid fa-paper-plane text-4xl"></i>
                      <p class="text-base font-semibold">
                        No invitations found
                      </p>
                      <p class="text-xs">
                        Send your first invitation by clicking "Invite
                        Students".
                      </p>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </div>
  }
</div>

<!-- Student Details Drawer -->
@if (showDetailsDrawer && selectedStudent) {
  <div class="fixed inset-0 z-40 flex justify-end">
    <div
      class="fixed inset-0 bg-black/40 backdrop-blur-sm"
      (click)="closeStudentDetails()"
    ></div>
    <aside
      class="relative w-full max-w-md bg-base-100 shadow-2xl flex flex-col overflow-y-auto z-50 animate-in slide-in-from-right duration-200"
    >
      <!-- Drawer Header -->
      <div
        class="flex items-center justify-between p-5 border-b border-base-200"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"
          >
            <i class="fa-solid fa-user-graduate"></i>
          </div>
          <h2 class="font-bold text-lg text-base-content">Student Profile</h2>
        </div>
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square"
          (click)="closeStudentDetails()"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>

      <!-- Drawer Content -->
      <div class="p-5 space-y-5">
        <!-- Identity Summary -->
        <div
          class="flex items-center gap-4 p-4 bg-base-200/50 rounded-2xl border border-base-200"
        >
          <div class="daisy-avatar daisy-placeholder">
            <div
              class="bg-primary text-primary-content font-bold rounded-full w-14 h-14 text-xl flex items-center justify-center"
            >
              {{ getStudentInitial(selectedStudent) }}
            </div>
          </div>
          <div>
            <div class="flex items-center gap-2">
              <h3 class="font-bold text-lg text-base-content leading-tight">
                {{ getStudentName(selectedStudent) }}
              </h3>
              @if (selectedStudent.isManuallyAdded) {
                <span
                  class="daisy-badge daisy-badge-xs daisy-badge-warning gap-1 font-medium"
                >
                  <i class="fa-solid fa-clipboard-user text-[9px]"></i> Manual
                </span>
              }
            </div>
            <p class="text-xs text-base-content/60 mt-0.5 font-mono">
              ID: {{ selectedStudent.studentId || selectedStudent._id }}
            </p>
            <div class="mt-2 flex items-center gap-2">
              <span
                class="daisy-badge daisy-badge-sm font-medium {{
                  getStatusBadgeClass(selectedStudent.status)
                }}"
              >
                {{ selectedStudent.status }}
              </span>
              <span class="daisy-badge daisy-badge-ghost daisy-badge-sm">
                Student
              </span>
            </div>
          </div>
        </div>

        <!-- Contact Information -->
        <div>
          <h4
            class="text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-2"
          >
            Contact & Address
          </h4>
          <div
            class="bg-base-100 border border-base-200 rounded-xl p-3 space-y-2 text-sm"
          >
            <div class="flex justify-between py-1 border-b border-base-200/60">
              <span class="text-base-content/60 text-xs">Email</span>
              <span class="font-medium text-base-content">{{
                getStudentEmail(selectedStudent) || 'Not provided'
              }}</span>
            </div>
            <div class="flex justify-between py-1 border-b border-base-200/60">
              <span class="text-base-content/60 text-xs">Mobile</span>
              <span class="font-medium text-base-content">{{
                getStudentMobile(selectedStudent) || 'Not provided'
              }}</span>
            </div>
            <div class="flex justify-between py-1">
              <span class="text-base-content/60 text-xs">City / District</span>
              <span class="font-medium text-base-content">{{
                selectedStudent.profile.address?.city ||
                  selectedStudent.profile.address?.district ||
                  'Not specified'
              }}</span>
            </div>
          </div>
        </div>

        <!-- Subscribed Live Classes in Academy -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <h4
              class="text-xs font-semibold uppercase tracking-wider text-base-content/60"
            >
              Enrolled Live Classes
            </h4>
            <button
              type="button"
              class="daisy-btn daisy-btn-xs daisy-btn-primary gap-1"
              (click)="openAssignModal()"
            >
              <i class="fa-solid fa-plus"></i> Assign Class
            </button>
          </div>
          @if (loadingSummary) {
            <div class="flex justify-center py-6">
              <span
                class="daisy-loading daisy-loading-spinner daisy-loading-sm text-primary"
              ></span>
            </div>
          } @else {
            @if (
              studentSummary?.subscriptions &&
              (studentSummary?.subscriptions?.length || 0) > 0
            ) {
              <div class="space-y-2">
                @for (sub of studentSummary?.subscriptions; track sub._id) {
                  <div
                    class="p-3 bg-base-200/60 border border-base-200 rounded-xl flex flex-col gap-2"
                  >
                    <div class="flex items-center justify-between">
                      <div>
                        <p class="font-semibold text-sm text-base-content">
                          {{
                            sub.liveClass?.title || 'Live Class Subscription'
                          }}
                        </p>
                        <p class="text-xs text-base-content/50 mt-0.5">
                          Type: {{ sub.paymentType }}
                        </p>
                      </div>
                      <span
                        class="daisy-badge daisy-badge-sm {{
                          getStatusBadgeClass(sub.status)
                        }}"
                      >
                        {{ sub.status }}
                      </span>
                    </div>

                    <!-- Subscription Actions -->
                    <div
                      class="flex items-center justify-end gap-1.5 pt-1 border-t border-base-200/60"
                    >
                      @if (sub.status === 'ACTIVE') {
                        <button
                          type="button"
                          class="daisy-btn daisy-btn-xs daisy-btn-warning daisy-btn-outline gap-1"
                          (click)="pauseSubscription(sub)"
                          [disabled]="modifyingSubscription() === sub._id"
                          title="Pause subscription"
                        >
                          <i class="fa-solid fa-pause"></i> Pause
                        </button>
                      }
                      @if (sub.status === 'SUSPENDED') {
                        <button
                          type="button"
                          class="daisy-btn daisy-btn-xs daisy-btn-success daisy-btn-outline gap-1"
                          (click)="resumeSubscription(sub)"
                          [disabled]="modifyingSubscription() === sub._id"
                          title="Resume subscription"
                        >
                          <i class="fa-solid fa-play"></i> Resume
                        </button>
                      }
                      @if (
                        sub.status !== 'CANCELLED' && sub.status !== 'COMPLETE'
                      ) {
                        <button
                          type="button"
                          class="daisy-btn daisy-btn-xs daisy-btn-ghost text-error gap-1"
                          (click)="cancelStudentSubscription(sub)"
                          [disabled]="modifyingSubscription() === sub._id"
                          title="Cancel subscription"
                        >
                          <i class="fa-solid fa-xmark"></i> Cancel
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div
                class="p-4 text-center bg-base-200/30 rounded-xl border border-dashed border-base-300 text-xs text-base-content/50"
              >
                No active class subscriptions found for this student.
              </div>
            }
          }
        </div>

        <!-- Enrolled Courses in Academy -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <h4
              class="text-xs font-semibold uppercase tracking-wider text-base-content/60"
            >
              Enrolled Courses
            </h4>
            <button
              type="button"
              class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary gap-1"
              (click)="openAssignModal('COURSE')"
            >
              <i class="fa-solid fa-plus"></i> Enroll Course
            </button>
          </div>
          @if (loadingSummary) {
            <div class="flex justify-center py-4">
              <span
                class="daisy-loading daisy-loading-spinner daisy-loading-sm text-primary"
              ></span>
            </div>
          } @else {
            @if (
              studentSummary?.enrollments &&
              (studentSummary?.enrollments?.length || 0) > 0
            ) {
              <div class="space-y-2">
                @for (enr of studentSummary?.enrollments; track enr._id) {
                  <div
                    class="p-3 bg-base-200/60 border border-base-200 rounded-xl flex flex-col gap-2"
                  >
                    <div class="flex items-center justify-between">
                      <div class="min-w-0 flex-1 pr-2">
                        <p
                          class="font-semibold text-sm text-base-content truncate"
                        >
                          {{ enr.course?.title || 'Self-Paced Course' }}
                        </p>
                        <p class="text-xs text-base-content/50 mt-0.5">
                          @if (enr.expiresOn) {
                            Expires: {{ enr.expiresOn | date: 'mediumDate' }}
                          } @else {
                            Lifetime Access
                          }
                        </p>
                      </div>
                      <span
                        class="daisy-badge daisy-badge-sm"
                        [ngClass]="
                          isEnrollmentExpired(enr)
                            ? 'daisy-badge-error text-white'
                            : 'daisy-badge-success text-white'
                        "
                      >
                        {{ isEnrollmentExpired(enr) ? 'EXPIRED' : 'ACTIVE' }}
                      </span>
                    </div>

                    <!-- Enrollment Actions -->
                    <div
                      class="flex items-center justify-end gap-1.5 pt-1 border-t border-base-200/60"
                    >
                      <button
                        type="button"
                        class="daisy-btn daisy-btn-xs daisy-btn-ghost text-error gap-1"
                        (click)="revokeCourseEnrollment(enr)"
                        title="Revoke course access"
                      >
                        <i class="fa-solid fa-user-minus"></i> Remove Access
                      </button>
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div
                class="p-4 text-center bg-base-200/30 rounded-xl border border-dashed border-base-300 text-xs text-base-content/50"
              >
                No course enrollments found for this student.
              </div>
            }
          }
        </div>

        <!-- Management Actions -->
        <div class="pt-4 border-t border-base-200 space-y-2">
          @if (selectedStudent.isManuallyAdded) {
            <button
              type="button"
              class="daisy-btn daisy-btn-sm daisy-btn-outline daisy-btn-warning w-full gap-2"
              (click)="openAdminResetPassword(selectedStudent)"
            >
              <i class="fa-solid fa-key"></i>
              Reset Student Password
            </button>
          }
          <button
            type="button"
            class="daisy-btn daisy-btn-sm w-full gap-2 {{
              selectedStudent.status === 'BLOCKED'
                ? 'daisy-btn-success'
                : 'daisy-btn-error'
            }}"
            (click)="toggleBlockStudent(selectedStudent)"
            [disabled]="processingAction()"
          >
            <i
              class="fa-solid {{
                selectedStudent.status === 'BLOCKED' ? 'fa-lock-open' : 'fa-ban'
              }}"
            ></i>
            {{
              selectedStudent.status === 'BLOCKED'
                ? 'Unblock Student'
                : 'Block Student'
            }}
          </button>
        </div>
      </div>
    </aside>
  </div>
}

<!-- Reusable Invite Modal -->
<black-invite-student-modal
  [isOpen]="showInviteModal"
  [availableLiveClasses]="availableClasses"
  [availableCourses]="availableCourses"
  (invited)="loadInvitations(); loadStudents()"
  (close)="showInviteModal = false"
></black-invite-student-modal>

<!-- Assign Learning Access Modal -->
@if (showAssignModal && selectedStudent) {
  <dialog class="daisy-modal daisy-modal-open z-50">
    <div class="daisy-modal-box max-w-md">
      <h3 class="font-bold text-lg mb-1 text-base-content">
        {{
          assignType === 'LIVE_CLASS'
            ? 'Assign Class Subscription'
            : 'Assign Course Enrollment'
        }}
      </h3>
      <p class="text-xs text-base-content/60 mb-4">
        Assign learning access directly to
        <span class="font-medium text-base-content">{{
          getStudentName(selectedStudent)
        }}</span
        >.
      </p>

      <!-- Type Switcher -->
      <div class="grid grid-cols-2 gap-2 bg-base-200 p-1 rounded-xl mb-4">
        <button
          type="button"
          class="daisy-btn daisy-btn-xs border-0 flex items-center justify-center gap-1.5 transition-all"
          [ngClass]="
            assignType === 'LIVE_CLASS'
              ? 'daisy-btn-primary shadow-sm'
              : 'daisy-btn-ghost hover:bg-base-300/50'
          "
          (click)="assignType = 'LIVE_CLASS'"
        >
          <i class="fa-solid fa-video"></i>
          <span>Live Class</span>
        </button>
        <button
          type="button"
          class="daisy-btn daisy-btn-xs border-0 flex items-center justify-center gap-1.5 transition-all"
          [ngClass]="
            assignType === 'COURSE'
              ? 'daisy-btn-primary shadow-sm'
              : 'daisy-btn-ghost hover:bg-base-300/50'
          "
          (click)="assignType = 'COURSE'"
        >
          <i class="fa-solid fa-graduation-cap"></i>
          <span>Course</span>
        </button>
      </div>

      <div class="space-y-3">
        @if (assignType === 'LIVE_CLASS') {
          <div class="daisy-form-control">
            <label class="daisy-label">
              <span class="daisy-label-text font-medium text-xs"
                >Select Live Class</span
              >
            </label>
            @if (loadingLearningItems) {
              <div
                class="flex items-center gap-2 py-2 text-sm text-base-content/60"
              >
                <span
                  class="daisy-loading daisy-loading-spinner daisy-loading-xs text-primary"
                ></span>
                <span>Loading classes...</span>
              </div>
            } @else if (availableClasses.length === 0) {
              <p class="text-xs text-error">
                No published classes available to assign.
              </p>
            } @else {
              <select
                class="daisy-select daisy-select-sm daisy-select-bordered w-full"
                [(ngModel)]="selectedClassId"
              >
                <option value="" disabled>Select a class...</option>
                @for (c of availableClasses; track (c._id || c.id)) {
                  <option [value]="c._id || c.id">{{ c.title }} {{ c.settings?.isPublic === false ? '(Unlisted)' : '' }}</option>
                }
              </select>
            }
          </div>

          <div class="daisy-form-control">
            <label class="daisy-label">
              <span class="daisy-label-text font-medium text-xs"
                >Payment Schedule</span
              >
            </label>
            <select
              class="daisy-select daisy-select-sm daisy-select-bordered w-full"
              [(ngModel)]="selectedPaymentType"
            >
              <option value="MONTHLY">Monthly</option>
              <option value="TOTAL">One-time / Full</option>
            </select>
          </div>
        } @else {
          <div class="daisy-form-control">
            <label class="daisy-label">
              <span class="daisy-label-text font-medium text-xs"
                >Select Course</span
              >
            </label>
            @if (loadingLearningItems) {
              <div
                class="flex items-center gap-2 py-2 text-sm text-base-content/60"
              >
                <span
                  class="daisy-loading daisy-loading-spinner daisy-loading-xs text-primary"
                ></span>
                <span>Loading courses...</span>
              </div>
            } @else if (availableCourses.length === 0) {
              <p class="text-xs text-error">No courses available to assign.</p>
            } @else {
              <select
                class="daisy-select daisy-select-sm daisy-select-bordered w-full"
                [(ngModel)]="selectedCourseId"
              >
                <option value="" disabled>Select a course...</option>
                @for (c of availableCourses; track (c._id || c.id)) {
                  <option [value]="c._id || c.id">{{ c.title }} {{ c.settings?.isPublic === false ? '(Unlisted)' : '' }}</option>
                }
              </select>
            }
          </div>

          <div class="daisy-form-control">
            <label class="daisy-label">
              <span class="daisy-label-text font-medium text-xs"
                >Access Duration</span
              >
            </label>
            <select
              class="daisy-select daisy-select-sm daisy-select-bordered w-full"
              [(ngModel)]="selectedCourseDurationDays"
            >
              <option [value]="30">30 Days</option>
              <option [value]="90">90 Days (3 Months)</option>
              <option [value]="180">180 Days (6 Months)</option>
              <option [value]="365">1 Year (365 Days)</option>
              <option [value]="1825">5 Years / Lifetime</option>
            </select>
          </div>
        }
      </div>

      <div class="daisy-modal-action">
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm"
          (click)="closeAssignModal()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2"
          (click)="submitAssignSubscription()"
          [disabled]="
            submittingAssign() ||
            (assignType === 'LIVE_CLASS' ? !selectedClassId : !selectedCourseId)
          "
        >
          @if (submittingAssign()) {
            <span
              class="daisy-loading daisy-loading-spinner daisy-loading-xs"
            ></span>
          }
          {{
            assignType === 'LIVE_CLASS'
              ? 'Assign Subscription'
              : 'Enroll Student'
          }}
        </button>
      </div>
    </div>
    <div class="daisy-modal-backdrop" (click)="closeAssignModal()"></div>
  </dialog>
}

<!-- Add Offline / Manual Student Modal -->
@if (showAddManualStudentModal) {
  <dialog class="daisy-modal daisy-modal-open z-50">
    <div class="daisy-modal-box max-w-lg">
      <div
        class="flex items-center justify-between pb-3 border-b border-base-200 mb-4"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"
          >
            <i class="fa-solid fa-user-plus"></i>
          </div>
          <div>
            <h3 class="font-bold text-lg text-base-content leading-tight">
              Add Student
            </h3>
            <p class="text-xs text-base-content/60">
              Create an account directly and dispatch login credentials
            </p>
          </div>
        </div>
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square"
          (click)="closeAddManualStudentModal()"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>

      <div class="space-y-3">
        <!-- Name Fields -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="daisy-form-control">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-medium text-xs"
                >First Name <span class="text-error">*</span></span
              >
            </label>
            <input
              type="text"
              class="daisy-input daisy-input-sm daisy-input-bordered w-full"
              placeholder="e.g. John"
              [(ngModel)]="manualStudentForm.firstName"
            />
          </div>
          <div class="daisy-form-control">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-medium text-xs"
                >Last Name</span
              >
            </label>
            <input
              type="text"
              class="daisy-input daisy-input-sm daisy-input-bordered w-full"
              placeholder="e.g. Doe"
              [(ngModel)]="manualStudentForm.lastName"
            />
          </div>
        </div>

        <!-- Contact Fields -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="daisy-form-control">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-medium text-xs"
                >Email Address</span
              >
            </label>
            <input
              type="email"
              class="daisy-input daisy-input-sm daisy-input-bordered w-full"
              placeholder="student@example.com"
              [(ngModel)]="manualStudentForm.email"
            />
          </div>
          <div class="daisy-form-control">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-medium text-xs"
                >Mobile Number</span
              >
            </label>
            <input
              type="tel"
              class="daisy-input daisy-input-sm daisy-input-bordered w-full"
              placeholder="e.g. +94771234567"
              [(ngModel)]="manualStudentForm.mobile"
            />
          </div>
        </div>
        <p class="text-[11px] text-base-content/50 italic px-1">
          * At least one contact method (email or mobile) is required to receive
          login credentials.
        </p>

        <!-- Student ID Override -->
        <div class="daisy-form-control">
          <label class="daisy-label py-1">
            <span class="daisy-label-text font-medium text-xs"
              >Student ID (Optional)</span
            >
            <span class="daisy-label-text-alt text-base-content/50"
              >Auto-generated if left blank</span
            >
          </label>
          <input
            type="text"
            class="daisy-input daisy-input-sm daisy-input-bordered font-mono w-full"
            placeholder="e.g. STU-2026-0042"
            [(ngModel)]="manualStudentForm.studentId"
          />
        </div>

        <!-- Optional Direct Learning Item Assignment -->
        <div class="pt-2 border-t border-base-200 space-y-3">
          <p class="text-xs font-semibold text-base-content/70">
            Initial Enrollment (Optional)
          </p>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="daisy-form-control">
              <label class="daisy-label py-1">
                <span class="daisy-label-text font-medium text-xs"
                  >Assign Live Class</span
                >
              </label>
              <select
                class="daisy-select daisy-select-sm daisy-select-bordered w-full"
                [(ngModel)]="manualStudentForm.liveClassId"
              >
                <option value="">None</option>
                @for (c of availableClasses; track (c._id || c.id)) {
                  <option [value]="c._id || c.id">{{ c.title }} {{ c.settings?.isPublic === false ? '(Unlisted)' : '' }}</option>
                }
              </select>
            </div>

            <div class="daisy-form-control">
              <label class="daisy-label py-1">
                <span class="daisy-label-text font-medium text-xs"
                  >Enroll in Course</span
                >
              </label>
              <select
                class="daisy-select daisy-select-sm daisy-select-bordered w-full"
                [(ngModel)]="manualStudentForm.courseId"
              >
                <option value="">None</option>
                @for (crs of availableCourses; track (crs._id || crs.id)) {
                  <option [value]="crs._id || crs.id">{{ crs.title }} {{ crs.settings?.isPublic === false ? '(Unlisted)' : '' }}</option>
                }
              </select>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="daisy-form-control">
          <label class="daisy-label py-1">
            <span class="daisy-label-text font-medium text-xs"
              >Internal Notes</span
            >
          </label>
          <textarea
            class="daisy-textarea daisy-textarea-sm daisy-textarea-bordered w-full h-16"
            placeholder="e.g. Walk-in cash registration, Batch A..."
            [(ngModel)]="manualStudentForm.notes"
          ></textarea>
        </div>
      </div>

      <div class="daisy-modal-action mt-5">
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm"
          (click)="closeAddManualStudentModal()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2"
          (click)="submitAddManualStudent()"
          [disabled]="
            submittingManualStudent() ||
            !manualStudentForm.firstName.trim() ||
            (!manualStudentForm.email.trim() &&
              !manualStudentForm.mobile.trim())
          "
        >
          @if (submittingManualStudent()) {
            <span
              class="daisy-loading daisy-loading-spinner daisy-loading-xs"
            ></span>
          }
          <i class="fa-solid fa-user-check"></i>
          Onboard & Send Credentials
        </button>
      </div>
    </div>
    <div
      class="daisy-modal-backdrop"
      (click)="closeAddManualStudentModal()"
    ></div>
  </dialog>
}

<!-- Admin Reset Password Modal -->
@if (showResetPasswordModal && resetPasswordStudent) {
  <dialog class="daisy-modal daisy-modal-open z-50">
    <div class="daisy-modal-box max-w-sm">
      <div
        class="flex items-center justify-between pb-3 border-b border-base-200 mb-4"
      >
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 rounded-lg bg-warning/10 text-warning flex items-center justify-center"
          >
            <i class="fa-solid fa-key"></i>
          </div>
          <div>
            <h3 class="font-bold text-base text-base-content leading-tight">
              Reset Password
            </h3>
            <p class="text-xs text-base-content/60">
              For {{ getStudentName(resetPasswordStudent) }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square"
          (click)="closeResetPasswordModal()"
        >
          <i class="fa-solid fa-xmark text-lg"></i>
        </button>
      </div>

      <form
        autocomplete="off"
        (ngSubmit)="$event.preventDefault(); submitAdminResetPassword()"
      >
        <!-- Isolated hidden username prevents password managers from autofilling preceding search input -->
        <input
          type="text"
          name="username"
          [value]="resetPasswordStudent.username"
          autocomplete="username"
          class="hidden"
          tabindex="-1"
          aria-hidden="true"
        />

        <div class="space-y-3">
          <div class="p-3 bg-base-200/60 rounded-xl text-xs space-y-1">
            <p class="text-base-content/70">
              <strong>Username:</strong> {{ resetPasswordStudent.username }}
            </p>
            <p class="text-base-content/70">
              <strong>Student ID:</strong>
              {{ resetPasswordStudent.studentId || resetPasswordStudent._id }}
            </p>
          </div>

          <div class="daisy-form-control">
            <label class="daisy-label py-1">
              <span class="daisy-label-text font-medium text-xs"
                >New Password <span class="text-error">*</span></span
              >
            </label>
            <input
              type="password"
              name="newPassword"
              autocomplete="new-password"
              data-lpignore="true"
              class="daisy-input daisy-input-sm daisy-input-bordered w-full"
              placeholder="Minimum 6 characters"
              [(ngModel)]="newPassword"
            />
          </div>

          <p class="text-[11px] text-base-content/50">
            This account was manually added by staff. Setting a new password
            allows the student to log in with it immediately.
          </p>
        </div>

        <div class="daisy-modal-action mt-4">
          <button
            type="button"
            class="daisy-btn daisy-btn-ghost daisy-btn-sm"
            (click)="closeResetPasswordModal()"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="daisy-btn daisy-btn-warning daisy-btn-sm gap-2"
            [disabled]="
              submittingResetPassword() ||
              !newPassword ||
              newPassword.length < 6
            "
          >
            @if (submittingResetPassword()) {
              <span
                class="daisy-loading daisy-loading-spinner daisy-loading-xs"
              ></span>
            }
            <i class="fa-solid fa-check"></i>
            Update Password
          </button>
        </div>
      </form>
    </div>
    <div class="daisy-modal-backdrop" (click)="closeResetPasswordModal()"></div>
  </dialog>
}
